import React, { useState } from 'react';
import { Box, Typography, Button, Card, CardContent, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import ReversalUploadCard from '../components/ReversalUploadCard';
import { downloadReversalSampleExcel } from '../utils/reversalSampleGenerator';
interface TransactionReversalProps {
  onLogout: () => void;
}
const drawerWidth = 240;
const TransactionReversal: React.FC<TransactionReversalProps> = ({
  onLogout
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUploadCard, setShowUploadCard] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    bgcolor: 'background.default'
  }} data-id="element-4445" data-mp="25g7m2mh2axpw">
      <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} onLogout={onLogout} title="Transaction Reversal" data-id="element-4446" data-mp="yzcbfk7czjno" />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} activePage="transaction-reversal" data-id="element-4447" data-mp="u1aryy60tibo" />

      <Box component="main" sx={{
      flexGrow: 1,
      width: {
        xs: '100%',
        sm: `calc(100% - ${drawerWidth}px)`
      },
      maxWidth: '100%',
      ml: 0,
      mt: 8,
      py: 2,
      px: {
        xs: 2,
        sm: 3,
        md: 5
      },
      overflow: 'hidden',
      bgcolor: 'background.default'
    }} data-id="element-4448" data-mp="15b9is1v8inpe">
        {!showUploadCard ? <Card elevation={0} sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        background: isDark ? 'linear-gradient(145deg, rgba(28,27,54,0.98) 0%, rgba(22,21,44,0.98) 100%)' : 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)',
        textAlign: 'center',
        py: {
          xs: 4,
          md: 6
        },
        minHeight: 'calc(100vh - 112px)'
      }} data-id="element-4449" data-mp="hjkmybj7fh3p">
            <CardContent sx={{
          maxWidth: 760,
          mx: 'auto'
        }} data-id="element-4450" data-mp="3nql2cguls03">
              <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.14) 0%, rgba(220, 38, 38, 0.14) 100%)',
            mb: 3
          }} data-id="element-4451" data-mp="25bv2urusdvfg">
                <Icon icon="lucide:undo-2" width={40} height={40} color="#EF4444" data-id="element-4452" data-mp="2k8cgjpqg7m56" />
              </Box>

              <Typography variant="h5" gutterBottom fontWeight={700} data-id="element-4453" data-mp="2f997zvb9exsg">
                Reverse Posted Transactions
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{
            mb: 4,
            maxWidth: 620,
            mx: 'auto'
          }} data-id="element-4454" data-mp="2a93gcf4zpbad">
                Upload a file containing transaction details to reverse already posted transactions. The system will validate the data,
                match it against existing transactions, and process reversals.
              </Typography>

              <Box sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'center',
            flexWrap: 'wrap'
          }} data-id="element-4455" data-mp="op85uomrxeem">
                <Button variant="outlined" startIcon={<Icon icon="lucide:download" width={18} data-id="element-4457" data-mp="ynxm6cz0uqsr" />} onClick={downloadReversalSampleExcel} sx={{
              borderColor: 'divider',
              color: 'text.primary'
            }} data-id="element-4456" data-mp="tnt7mshoc656">
                  Download Sample File
                </Button>
                <Button variant="contained" startIcon={<Icon icon="lucide:upload" width={18} data-id="element-4459" data-mp="1hfzy5b0pxiwo" />} onClick={() => setShowUploadCard(true)} sx={{
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)'
              }
            }} data-id="element-4458" data-mp="1cflzgjl0blbb">
                  Upload Reversal File
                </Button>
              </Box>

              <Box sx={{
            mt: 6,
            p: 3,
            backgroundColor: isDark ? 'rgba(14, 165, 233, 0.08)' : 'rgba(14, 165, 233, 0.04)',
            borderRadius: 2,
            border: '1px solid rgba(14, 165, 233, 0.18)',
            textAlign: 'left'
          }} data-id="element-4460" data-mp="1igcjsvmok4e9">
                <Typography variant="subtitle2" gutterBottom fontWeight={700} color="primary.main" data-id="element-4461" data-mp="yt52aoy8qivy">
                  Required File Columns:
                </Typography>
                <Box component="ul" sx={{
              mt: 2,
              pl: 2
            }} data-id="element-4462" data-mp="18jpvtxz24x80">
                  {[['Original Txn Date', 'Date of the original transaction (YYYY-MM-DD)'], ['Txn Rail', 'Transaction rail (1link, RAAST, or RTGS)'], ['Partner', 'Name of the partner/MTO'], ['Txn Amount', 'Transaction amount (numeric)'], ['CBS Reference', 'CBS reference (optional)'], ['Txn Reference', 'STAN (6-digit for 1link), MessageId (3-35 chars for RAAST), or uniqueId (for RTGS)'], ['Receiver IBAN', 'IBAN of the receiver (PK + 22 digits)'], ['Receiver Name', 'Name of the receiver']].map(([label, value]) => <Typography key={label} component="li" variant="body2" sx={{
                mb: 1
              }} data-id="element-4463" data-mp="2iqlu2qy80qqr">
                      <strong data-id="element-4464" data-mp="36gznolwgexng">{label}:</strong> {value}
                    </Typography>)}
                </Box>
              </Box>

              <Box sx={{
            mt: 3,
            p: 2,
            backgroundColor: isDark ? 'rgba(245, 158, 11, 0.08)' : 'rgba(245, 158, 11, 0.04)',
            borderRadius: 2,
            border: '1px solid rgba(245, 158, 11, 0.18)',
            textAlign: 'left'
          }} data-id="element-4465" data-mp="2whh62c40nnyk">
                <Box sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }} data-id="element-4466" data-mp="1q2q1nyfopkzs">
                  <Icon icon="lucide:info" width={18} height={18} color="#F59E0B" style={{
                marginTop: 2,
                flexShrink: 0
              }} data-id="element-4467" data-mp="15y0otp0e2efj" />
                  <Box data-id="element-4468" data-mp="11zti7mzn2ti2">
                    <Typography variant="subtitle2" gutterBottom fontWeight={700} color="#F59E0B" data-id="element-4469" data-mp="x70cowwcjh4x">
                      Sample File Contains Test Data
                    </Typography>
                    <Typography variant="body2" color="text.secondary" data-id="element-4470" data-mp="s62w2oqziv5j">
                      The sample file includes 7 demo transactions with realistic data. You can use these to test the reversal process or
                      modify them with your actual transaction details. The demo data follows all validation rules and formats.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card> : <ReversalUploadCard onClose={() => setShowUploadCard(false)} data-id="element-4471" data-mp="28k2t9ryg46p3" />}
      </Box>
    </Box>;
};
export default TransactionReversal;